import { useMemo, useState } from 'react';
import { readApiError, roverNotificationsApi } from '@/api/Rover/notifications/roverNotifications';
import {
  CHANNEL_LABELS,
  type Channel,
  type ChannelContent,
  type MergeValues,
  type MessageOption,
  type OptionsFormat,
} from '@/api/Rover/notifications/roverNotifications.types';
import { renderMessage } from '@/features/ApplicationForm/utils/notifications/renderMessage';
import { Button, Modal, Notice, Segmented } from '../ui/primitives';
import { EmailPreview, OnscreenPreview, SmsPreview } from './MessagePreviews';

export const SAMPLE_MERGE_VALUES: MergeValues = {
  applicant_name: 'Jordan Sample',
  application_id: 'SAMPLE-0001',
};

export interface ChannelPreviewModalProps {
  open: boolean;
  onClose: () => void;
  formId: string;
  programName: string;
  status: string;
  statusLabel: string;
  channel: Channel;
  /** Draft content, including unsaved edits. */
  content: ChannelContent;
  defaults: ChannelContent;
  options: MessageOption[];
  optionsFormat: OptionsFormat;
  /** Sender shown in the preview header, e.g. "Community Development <noreply@…>". */
  emailFrom?: string;
  smsFrom?: string;
  enableTestSend?: boolean;
}

export function ChannelPreviewModal(props: ChannelPreviewModalProps) {
  const {
    open,
    onClose,
    formId,
    programName,
    status,
    statusLabel,
    channel,
    content,
    defaults,
    options,
    optionsFormat,
    enableTestSend = true,
  } = props;
  const [width, setWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [testState, setTestState] = useState<{ kind: 'idle' | 'sending' | 'sent' | 'error'; text?: string }>({
    kind: 'idle',
  });

  const active = useMemo(() => options.filter((o) => o.isActive), [options]);
  const message = useMemo(
    () =>
      renderMessage({
        channel,
        content,
        defaults,
        mergeValues: { ...SAMPLE_MERGE_VALUES, program_name: programName, status: statusLabel },
        activeOptionCount: active.length,
        selectedOptions: active.slice(0, 2),
        optionsFormat,
      }),
    [channel, content, defaults, programName, statusLabel, active, optionsFormat]
  );

  const sendTest = async () => {
    if (channel === 'onscreen') return;
    setTestState({ kind: 'sending' });
    try {
      const res = await roverNotificationsApi.sendTest(formId, status, { channel, content });
      setTestState({ kind: 'sent', text: `Test sent to ${res.sentTo}.` });
    } catch (e) {
      setTestState({ kind: 'error', text: readApiError(e).message });
    }
  };

  const close = () => {
    setTestState({ kind: 'idle' });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size='xl'
      title={`${CHANNEL_LABELS[channel]} preview · ${statusLabel}`}
      description={
        active.length > 0
          ? `Shown with a sample applicant and ${Math.min(2, active.length)} sample option${active.length > 1 ? 's' : ''}.`
          : 'Shown with a sample applicant.'
      }
      footer={
        <div className='flex items-center justify-between gap-4'>
          <div className='text-sm text-gray-600' aria-live='polite'>
            {testState.kind === 'sent' || testState.kind === 'error' ? (
              <span className={testState.kind === 'error' ? 'text-red-700' : 'text-emerald-800'}>{testState.text}</span>
            ) : (
              'Unsaved edits are included in the preview.'
            )}
          </div>
          <div className='flex gap-3'>
            {enableTestSend && channel !== 'onscreen' && (
              <Button onClick={sendTest} disabled={testState.kind === 'sending'}>
                {testState.kind === 'sending' ? 'Sending…' : channel === 'sms' ? 'Send test text to me' : 'Send test email to me'}
              </Button>
            )}
            <Button variant='primary' onClick={close}>
              Done
            </Button>
          </div>
        </div>
      }
    >
      {channel !== 'sms' && (
        <div className='flex justify-end px-6 pt-4'>
          <Segmented
            label='Preview width'
            value={width}
            onChange={setWidth}
            options={[
              { value: 'desktop', label: 'Desktop' },
              { value: 'mobile', label: 'Mobile' },
            ]}
          />
        </div>
      )}
      {message.unknownTokens.length > 0 && (
        <div className='px-6 pt-4'>
          <Notice tone='warning'>
            Unknown merge field{message.unknownTokens.length > 1 ? 's' : ''}: {message.unknownTokens.map((t) => `{{${t}}}`).join(', ')}. It
            will be sent as written.
          </Notice>
        </div>
      )}
      <div className='pt-4'>
        {channel === 'email' && (
          <EmailPreview
            message={message}
            from={props.emailFrom ?? 'Program sender <[SENDER EMAIL]>'}
            to='jordan.sample@example.com'
            programName={programName}
            width={width}
          />
        )}
        {channel === 'sms' && <SmsPreview message={message} to='(205) 555-0100' from={props.smsFrom ?? '[PROGRAM SMS NUMBER]'} />}
        {channel === 'onscreen' && <OnscreenPreview message={message} statusLabel={statusLabel} width={width} />}
      </div>
    </Modal>
  );
}
